import type { SynergyFigure } from './synergy-risk';
import type { DecisionAgentSourceClass } from './contracts/sourceClasses';
import labels from './ask-helix/contracts/synergy-view.labels.json';

export const SYNERGY_VIEW_LABELS = labels;

/** One vocabulary for figures. Scope and period belong to the containing row/view.
 * Reported value capture is not an assertion that the ledger verified it.
 * Changes retain their measure label and explicitly name the comparison period.
 */
export const VIEW_FIGURE_LABELS = {
  ledgerVerified: 'ledger-verified',
  realized: 'reported',
  realizedChange: 'reported change',
  planToDate: 'Plan to date',
  commitment: 'Commitment',
  committed: 'committed',
  decisionTarget: 'decision target',
  behindPlan: 'behind plan',
  workDaysLate: 'days late',
  erpWorkDaysLate: 'ERP days late',
  commitmentFinishDaysLate: labels.finishDaysLate,
} as const;

export type ViewFigureConcept = keyof typeof VIEW_FIGURE_LABELS;

/** Reconciliation reader wording; published measure identities and labels stay intact. */
export const RECONCILIATION_LABELS = {
  reported: 'Reported',
  ledgerVerified: VIEW_FIGURE_LABELS.ledgerVerified,
  deliveryGapTitle: 'Delivery gap',
  planUnavailableHeading: 'Plan unavailable: {label}.',
  deliveryGap: 'Delivery gap: {label}. Committed {committed}; ledger-verified {ledger}; '
    + '{gap} of the commitment not yet ledger-verified.',
  deliveryGapOverVerified: 'Delivery gap: {label}. Committed {committed}; '
    + 'ledger-verified {ledger}; gap {gap} (ledger-verified exceeds the commitment).',
  deliveryGapWithoutLedger: 'Delivery gap: {label}. Committed {committed}; ledger-verified not supplied.',
  reportedCost: 'Reported cost {reported}; ledger-verified cost {ledger}: {gap} apart.',
  costSources: 'Sources: published reported costs and ledger postings',
  sources: 'Sources: published reported savings and ledger postings',
  basis: 'Annual run-rate',
} as const;

const LEDGER_SOURCE_WORDING: Readonly<Record<DecisionAgentSourceClass, string>> = {
  modeled: 'estimated pre-close accounts', observed: 'observed ledger',
  'connector-derived': 'connector-derived ledger', public: 'public ledger',
  'public-filing': 'public-filing ledger', industry_reference: 'industry-reference ledger',
  'synthetic-demo': 'synthetic demo ledger', 'manual-admin': 'manually supplied ledger',
  'csv-admin-upload': 'uploaded ledger', extrapolated: 'extrapolated ledger',
};

export function reconciliationLedgerSource(sourceClass?: DecisionAgentSourceClass): string {
  return sourceClass ? LEDGER_SOURCE_WORDING[sourceClass] : 'ledger source class not recorded';
}

/** Label only: retain the engine's exact value, display, certainty and evidence. */
export function labelViewFigure(figure: SynergyFigure, concept: ViewFigureConcept): SynergyFigure;
export function labelViewFigure(
  figure: SynergyFigure | null, concept: ViewFigureConcept,
): SynergyFigure | null;
export function labelViewFigure(
  figure: SynergyFigure | null, concept: ViewFigureConcept,
): SynergyFigure | null {
  return figure === null ? null : { ...figure, concept, label: VIEW_FIGURE_LABELS[concept] };
}
